import ButtonFill from '@components/ButtonFill';
import validationSchema from '@components/CustomForm/validationSchema';
import ErrorableField from '@components/Formik/ErrorableField';
import FormSwitch from '@components/Formik/FormSwitch';
import Link from '@components/Link';
import type {FormValues} from '@interfaces/CustomForm';
import type {FormikBag} from 'formik';
import {Formik, Form as FormikForm} from 'formik';
import type {ReactElement} from 'react';
import SumField from '@components/Formik/SumField';
import {localize} from '@services/i18n';
import I18n from '@components/I18n';

interface Props {
    title: string;
    subtitle?: string;
    withEditableSum: boolean;
    withRecurrentSwitch: boolean;
    buttonLabel: string;
    userAgreementLink: string;
    currency: string;
    initialValues: FormValues;
    disabled?: boolean;
    onSubmit(values: FormValues, formikBag: FormikBag<object, FormValues>): void;
}

export default function Form(props: Props): ReactElement<Props> {
    return (
        <Formik
            initialValues={props.initialValues}
            onSubmit={props.onSubmit}
            validationSchema={validationSchema}
        >
            {formik => (
                <FormikForm className="custom-form__form">
                    <div className="custom-form__title-block">
                        <div className="custom-form__title">{props.title}</div>
                        {props.subtitle ? (
                            <div className="custom-form__subtitle">{props.subtitle}</div>
                        ) : null}
                    </div>
                    <div className="custom-form__left-block">
                        <SumField
                            name="sum"
                            className="custom-form__sum"
                            withErrorMessage
                            disabled={props.disabled}
                            isEditable={props.withEditableSum}
                            value={formik.values.sum}
                            currency={props.currency}
                        />
                        {props.withRecurrentSwitch ? (
                            <ErrorableField
                                name="isRecurrent"
                                containerClassName="custom-form__recurrent"
                            >
                                {({field}) => (
                                    <FormSwitch
                                        {...field}
                                        label={localize('CustomForm_recurrentLabel')}
                                        disabled={props.disabled}
                                    />
                                )}
                            </ErrorableField>
                        ) : null}
                    </div>
                    <div className="custom-form__right-block">
                        <ErrorableField
                            name="email"
                            placeholder={localize('CustomForm_emailPlaceholder')}
                            withErrorMessage
                            disabled={props.disabled}
                        />
                        <Link className="custom-form__legal" to={props.userAgreementLink}>
                            <I18n code="CustomForm_userAgreementLabel" />
                        </Link>
                        {!props.disabled && (
                            <ButtonFill
                                type="submit"
                                className="custom-form__submit"
                                disabled={formik.isSubmitting}
                            >
                                {props.buttonLabel}
                            </ButtonFill>
                        )}
                    </div>
                </FormikForm>
            )}
        </Formik>
    );
}
